import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { dayTitle } from '../lib/planner.ts'

/**
 * ЧТО НОВОГО
 *
 * Семья редко сидит в приложении одновременно. Человек открывает его
 * вечером и хочет за полминуты понять, что изменилось за день, —
 * а не обходить десять разделов по очереди.
 *
 * Поэтому здесь не всё подряд, а то, о чём другому и правда интересно
 * узнать: добавили в список, купили, записали расход, поставили блюдо
 * в меню. Дневник здоровья, документы и брони подарков сюда не попадают
 * совсем — первые два личные, третьи убили бы сюрприз.
 */

type Event = {
  id: string
  action: string
  entityType: string
  entityId: string
  title: string
  href: string
  detail: string | null
  actor: { id: string; displayName: string; avatarUrl: string | null }
  createdAt: string
}

const VERBS: Record<string, Record<string, string>> = {
  created: {
    SHOPPING_ITEM: 'добавил в список',
    EVENT: 'завёл событие',
    RECIPE: 'записал рецепт',
    MEAL_PLAN: 'поставил в меню',
    TRANSACTION: 'записал',
    MEDIA_ITEM: 'добавил в кино',
    TICKET: 'положил билет',
    WISH_ITEM: 'хочет',
  },
  completed: {
    SHOPPING_ITEM: 'купил',
    TASK: 'сделал',
    MEDIA_ITEM: 'отметил просмотренным',
  },
  deleted: {},
  updated: {},
  commented: {},
  shared: {},
}

const ICONS: Record<string, string> = {
  SHOPPING_ITEM: '🛒',
  EVENT: '📅',
  TASK: '✅',
  RECIPE: '🍲',
  MEAL_PLAN: '🍽',
  TRANSACTION: '💰',
  MEDIA_ITEM: '🎬',
  TICKET: '🎫',
  WISH_ITEM: '🎁',
}

/** Отметка последнего просмотра — чтобы новое было видно значком. */
const SEEN_KEY = 'family.feed.seen'

export function readFeedSeen(): number {
  try {
    return Number(localStorage.getItem(SEEN_KEY) ?? 0)
  } catch {
    return 0
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, String(Date.now()))
  } catch {
    /* приватный режим — переживём */
  }
}

export function FeedPage() {
  const [events, setEvents] = useState<Event[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    api<{ events: Event[] }>('/api/feed')
      .then((data) => {
        setEvents(data.events)
        markSeen()
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }, [])

  // Группируем по дням: «сегодня» и «вчера» читаются лучше, чем время
  const byDay = new Map<string, Event[]>()
  for (const event of events ?? []) {
    const key = new Date(event.createdAt).toDateString()
    const found = byDay.get(key)
    if (found) found.push(event)
    else byDay.set(key, [event])
  }

  return (
    <div className="section">
      <header className="section-head">
        <h1>Что нового</h1>
      </header>

      <div className="section-body scroll">
        {events === null && !error && (
          <div className="center-screen" style={{ height: 80 }}>
            <div className="spinner" />
          </div>
        )}

        {events?.length === 0 && (
          <p className="empty-hint">
            Пока тихо. Здесь будет видно, что домашние добавили, купили и запланировали.
          </p>
        )}

        {[...byDay.entries()].map(([key, dayEvents]) => (
          <div key={key} className="day-block">
            <div className="day-head">{dayTitle(new Date(key))}</div>

            {dayEvents.map((event) => (
              <button
                key={event.id}
                className="feed-row"
                onClick={() => navigate(event.href)}
              >
                <Avatar
                  id={event.actor.id}
                  name={event.actor.displayName}
                  url={event.actor.avatarUrl}
                  size={32}
                />

                <span className="feed-text">
                  <span className="feed-line">
                    <b>{event.actor.displayName}</b>{' '}
                    {VERBS[event.action]?.[event.entityType] ?? 'изменил'}{' '}
                    <span className="feed-title">
                      {ICONS[event.entityType] ?? ''} {event.title}
                    </span>
                  </span>

                  <span className="feed-sub">
                    {event.detail && <span>{event.detail}</span>}
                    <span>
                      {new Date(event.createdAt).toLocaleTimeString('ru-RU', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        ))}

        {error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}
